"use client";

import Image from "next/image";
import { useTranslations, useLocale } from "next-intl";
import { Link, usePathname, useRouter } from "@/i18n/navigation";
import { GB, HU } from "country-flag-icons/react/3x2";
import { useEffect, useRef, useState } from "react";
import { FaBars, FaBook, FaEnvelope } from "react-icons/fa";

interface HeaderProps {
  name: string;
  desc: string;
  link?: string;
}

export default function Header({ name, desc, link }: HeaderProps) {
  const t = useTranslations("header");
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const lastScrollYRef = useRef(0);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const menuBtnRef = useRef<HTMLButtonElement | null>(null);
  const [panelEntering, setPanelEntering] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      setIsScrolled(currentY > 8);
      if (currentY > lastScrollYRef.current && currentY > 80) {
        setIsHidden(true);
      } else {
        setIsHidden(false);
      }
      lastScrollYRef.current = currentY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const panelEl = panelRef.current;
      const btnEl = menuBtnRef.current;
      const target = e.target as Node;
      if (panelEl && panelEl.contains(target)) return;
      if (btnEl && btnEl.contains(target)) return;
      setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    // trigger enter animation on next frame
    const id = requestAnimationFrame(() => setPanelEntering(true));
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown as EventListener);
      setPanelEntering(false);
    };
  }, [menuOpen]);

  const toggleLanguage = () => {
    const newLocale = locale === "hu" ? "en" : "hu";
    router.replace(pathname, { locale: newLocale });
  };

  const nextLocale = locale === "hu" ? "en" : "hu";

  return (
    <header className={`sticky top-0 z-50 border-b border-white/10 bg-gradient-to-r from-bme-main/20 via-gray-900/70 to-bme-main-hover/20 backdrop-blur-xl transform transition-transform duration-300 ${isHidden ? '-translate-y-full' : 'translate-y-0'} ${isScrolled ? 'shadow-lg' : ''}`}>
      <div className="container mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between relative">
          {/* Brand */}
          <div className="flex items-center gap-3 min-w-0">
            <Link href={link || '/'} className="rounded-lg hover:scale-105 transition-transform">
              <Image
                src="/BME.svg"
                alt="BME logo"
                width={120}
                height={36}
                className="h-8 sm:h-10 w-auto invert brightness-0 drop-shadow-lg"
              />
            </Link>
            <div className="hidden lg:block">
              <h1 className="text-base sm:text-xl font-semibold text-white truncate">
                {name}
              </h1>
              <p className="text-[11px] font-light text-white/70 truncate max-w-[53ch]">
                {desc}
              </p>
            </div>
          </div>

          {/* Centered Nav */}
          <nav className="hidden md:flex items-center gap-5 mx-4" aria-label="Main">
            <Link
              href="/docs"
              className="group inline-flex items-center gap-2 text-white/85 hover:text-white transition-colors"
            >
              <FaBook className="h-4 w-4 text-white/70 group-hover:text-white" aria-hidden="true" />
              {t('docs')}
            </Link>
            <Link
              href="/contact"
              className="group inline-flex items-center gap-2 text-white/85 hover:text-white transition-colors"
            >
              <FaEnvelope className="h-4 w-4 text-white/70 group-hover:text-white" aria-hidden="true" />
              {t('contact')}
            </Link>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={toggleLanguage}
              className="cursor-pointer inline-flex items-center gap-2 px-2.5 h-9 sm:px-3 sm:h-10 rounded-full border border-white/15 bg-white/10 text-white/90 hover:bg-white/15 hover:text-white shadow-md backdrop-blur-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
              aria-label={`Switch language to ${nextLocale.toUpperCase()}`}
              title={`Switch language to ${nextLocale.toUpperCase()}`}
              type="button"
            >
              {locale === 'hu' ? (
                <GB className="w-5 h-4 sm:w-6 sm:h-4" title="Switch to English" />
              ) : (
                <HU className="w-5 h-4 sm:w-6 sm:h-4" title="Váltás magyarra" />
              )}
              <span className="text-[10px] sm:text-xs font-medium tracking-wide uppercase">
                {nextLocale}
              </span>
            </button>

            <button
              ref={menuBtnRef}
              className="cursor-pointer md:hidden inline-flex items-center justify-center w-9 h-9 text-white hover:text-bme-main-hover rounded-lg"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="toggle-menu"
              type="button"
              aria-expanded={menuOpen}
              aria-controls="mobile-menu-panel"
            >
              <FaBars />
            </button>

            {/* Mobile side panel */}
            {menuOpen && (
              <>
                {/* Overlay for outside click */}
                <div
                  className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[0.5px] transition-opacity"
                  onClick={() => setMenuOpen(false)}
                  aria-hidden="true"
                />
                <div
                  ref={panelRef}
                  id="mobile-menu-panel"
                  role="dialog"
                  aria-modal="true"
                  className={`fixed right-2 top-2 z-50 w-[60vw] max-w-[13rem] rounded-2xl border border-black/10 bg-white/95 backdrop-blur-xl shadow-2xl transition-all duration-300 ease-out transform ${panelEntering ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'}`}
                  style={{ maxHeight: '70vh', overflow: 'auto' }}
                >
                  <div className="flex items-center justify-between px-3 py-2 border-b border-black/10">
                    <span className="text-xs font-medium text-black">{name}</span>
                    <button
                      className="cursor-pointer inline-flex items-center justify-center w-8 h-8 text-black hover:text-bme-main-hover rounded-md"
                      onClick={() => setMenuOpen(false)}
                      aria-label="close-menu"
                      type="button"
                    >
                      <FaBars />
                    </button>
                  </div>

                  <nav className="p-2" aria-label="Mobile">
                    <Link
                      href="/docs"
                      className="flex items-center gap-3 px-3 py-3 rounded-xl text-black hover:text-black hover:bg-black/5 transition"
                      onClick={() => setMenuOpen(false)}
                    >
                      <FaBook className="h-5 w-5 text-black" aria-hidden="true" />
                      <span className="text-base">{t('docs')}</span>
                    </Link>
                    <Link
                      href="/contact"
                      className="flex items-center gap-3 px-3 py-3 rounded-xl text-black hover:text-black hover:bg-black/5 transition"
                      onClick={() => setMenuOpen(false)}
                    >
                      <FaEnvelope className="h-5 w-5 text-black" aria-hidden="true" />
                      <span className="text-base">{t('contact')}</span>
                    </Link>
                  </nav>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
